import React, { useState, useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Loader from '../common/Loader';

const formatBytes = (bytes) => {
  if (bytes == null) return '—';
  if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(1)} GB`;
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(0)} MB`;
  return `${bytes} B`;
};

const PAGE_SIZES = [10, 20, 50, 100];

const getPageNumbers = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total]);
  for (let i = Math.max(1, current - 2); i <= Math.min(total, current + 2); i++) pages.add(i);
  const sorted = [...pages].sort((a, b) => a - b);
  const result = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push(`gap-${i}`);
    result.push(sorted[i]);
  }
  return result;
};

const QuotasTab = ({ orgId, apiCall, apiCallNoBody }) => {
  const base = `/admin/organizations/${orgId}`;

  const [quotas, setQuotas] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Multi-select
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Create/edit modal
  const [showModal, setShowModal] = useState(false);
  const [editQuota, setEditQuota] = useState(null);
  const [form, setForm] = useState({ user_id: '', daily_upload_limit: 50, daily_bytes_limit: 524288000 });
  const [saving, setSaving] = useState(false);

  // Delete
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Bulk delete
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);

  const fetchQuotas = async (p = page, s = pageSize) => {
    setLoading(true); setError('');
    try {
      const data = await apiCall(`${base}/quotas?page=${p}&page_size=${s}`);
      setQuotas(data.quotas || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchQuotas(page, pageSize); }, [page, pageSize]);

  // Multi-select
  const pageIds = quotas.map((q) => q.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
  const somePageSelected = pageIds.some((id) => selectedIds.has(id));

  const toggleSelect = (id) => {
    setSelectedIds((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  };
  const toggleSelectAll = () => {
    setSelectedIds((prev) => { const next = new Set(prev); if (allPageSelected) pageIds.forEach((id) => next.delete(id)); else pageIds.forEach((id) => next.add(id)); return next; });
  };

  // Open create/edit
  const openCreate = () => { setEditQuota(null); setForm({ user_id: '', daily_upload_limit: 50, daily_bytes_limit: 524288000 }); setShowModal(true); };
  const openEdit = (q) => { setEditQuota(q); setForm({ user_id: q.user_id || '', daily_upload_limit: q.daily_upload_limit ?? 50, daily_bytes_limit: q.daily_bytes_limit ?? 524288000 }); setShowModal(true); };
  const closeModal = () => { if (saving) return; setShowModal(false); setEditQuota(null); };

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      if (editQuota) {
        const payload = {};
        if (form.daily_upload_limit !== editQuota.daily_upload_limit) payload.daily_upload_limit = parseInt(form.daily_upload_limit);
        if (form.daily_bytes_limit !== editQuota.daily_bytes_limit) payload.daily_bytes_limit = parseInt(form.daily_bytes_limit);
        await apiCall(`${base}/quotas/${editQuota.id}`, { method: 'PUT', body: JSON.stringify(payload) });
      } else {
        await apiCall(`${base}/quotas`, { method: 'POST', body: JSON.stringify({ user_id: form.user_id.trim() || null, daily_upload_limit: parseInt(form.daily_upload_limit), daily_bytes_limit: parseInt(form.daily_bytes_limit) }) });
      }
      setShowModal(false); setEditQuota(null);
      await fetchQuotas(page, pageSize);
    } catch (err) { setError(err.message); }
    setSaving(false);
  };

  const handleDelete = async (quotaId) => {
    setDeleteLoading(true); setError('');
    try { await apiCallNoBody(`${base}/quotas/${quotaId}`, { method: 'DELETE' }); setDeleteId(null); await fetchQuotas(page, pageSize); }
    catch (err) { setError(err.message); }
    setDeleteLoading(false);
  };

  const handleBulkDelete = async () => {
    setBulkDeleteLoading(true); setError('');
    try {
      await Promise.all([...selectedIds].map((id) => apiCallNoBody(`${base}/quotas/${id}`, { method: 'DELETE' })));
      setSelectedIds(new Set()); setBulkDeleteConfirm(false);
      await fetchQuotas(page, pageSize);
    } catch (err) { setError(err.message); }
    setBulkDeleteLoading(false);
  };

  return (
    <>
      {error && <div className="error-message">{error}</div>}

      <div className="orgs-section-header" style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <button onClick={openCreate} className="btn btn-primary" style={{ fontSize: '0.8125rem', padding: '0.375rem 0.75rem' }}>
            <AddIcon sx={{ fontSize: '0.875rem' }} /> Add Quota
          </button>
          {selectedIds.size > 0 && (
            <button onClick={() => setBulkDeleteConfirm(true)} className="btn btn-danger" style={{ fontSize: '0.8125rem', padding: '0.375rem 0.75rem' }}>
              <DeleteSweepIcon sx={{ fontSize: '0.875rem' }} /> Delete Selected ({selectedIds.size})
            </button>
          )}
        </div>
        <span className="pagination-info">{total} quota(s)</span>
      </div>

      {loading ? <Loader size="small" /> : (
        <div className="table-card">
          <div className="users-table-wrapper">
            <table className="users-table">
              <thead>
                <tr>
                  <th className="th-checkbox"><input type="checkbox" checked={allPageSelected} ref={(el) => { if (el) el.indeterminate = somePageSelected && !allPageSelected; }} onChange={toggleSelectAll} /></th>
                  <th>Quota ID</th>
                  <th>Scope</th>
                  <th>Daily Upload Limit</th>
                  <th>Daily Bytes Limit</th>
                  <th>Used Today</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {quotas.length === 0 ? (
                  <tr><td colSpan={7} className="no-results">No quotas configured</td></tr>
                ) : quotas.map((q) => (
                  <tr key={q.id} className="user-row">
                    <td className="td-checkbox"><input type="checkbox" checked={selectedIds.has(q.id)} onChange={() => toggleSelect(q.id)} /></td>
                    <td className="td-id" title={q.id}>{q.id || '—'}</td>
                    <td>{q.user_id ? <span className="td-id" title={q.user_id}>{q.user_id}</span> : <em style={{ color: '#9ca3af' }}>Org-wide</em>}</td>
                    <td className="td-storage">{q.daily_upload_limit != null ? q.daily_upload_limit.toLocaleString() : '—'}</td>
                    <td className="td-storage">{formatBytes(q.daily_bytes_limit)}</td>
                    <td className="td-storage">{q.used_today != null ? `${q.used_today} / ${formatBytes(q.bytes_used_today)}` : '—'}</td>
                    <td className="td-actions">
                      <button className="btn-icon-sm" title="Edit" onClick={() => openEdit(q)}><EditIcon sx={{ fontSize: '1.125rem' }} /></button>
                      <button className="btn-icon-sm btn-delete" title="Delete" onClick={() => setDeleteId(q.id)}><DeleteOutlineIcon sx={{ fontSize: '1.125rem' }} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="table-footer">
            <div className="rows-per-page"><span>Rows per page:</span><select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); setSelectedIds(new Set()); }}>{PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}</select></div>
            <span className="pagination-info">{total === 0 ? 'No results' : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} of ${total}`}</span>
            {totalPages > 1 && (
              <div className="pagination">
                <button className="page-btn page-nav" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}><ChevronLeftIcon sx={{ fontSize: '1.25rem' }} /></button>
                {getPageNumbers(page, totalPages).map((p) => typeof p === 'string' ? <span key={p} className="page-ellipsis">…</span> : <button key={p} className={`page-btn${p === page ? ' active' : ''}`} onClick={() => setPage(p)}>{p}</button>)}
                <button className="page-btn page-nav" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}><ChevronRightIcon sx={{ fontSize: '1.25rem' }} /></button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create/Edit Quota Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h3 className="modal-title">{editQuota ? 'Edit Quota' : 'Create Quota'}</h3><button onClick={closeModal} className="btn-close" disabled={saving}><CloseIcon sx={{ fontSize: '1.25rem' }} /></button></div>
            <div className="modal-body">
              <div className="form-group"><label className="form-label">User ID</label><input type="text" value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })} className={`form-input${editQuota ? ' form-input-disabled' : ''}`} disabled={!!editQuota} placeholder="Leave empty for org-wide quota" /><p className="form-hint">Empty = org-wide quota. Provide a UUID for user-specific.</p></div>
              <div className="form-group"><label className="form-label">Daily Upload Limit</label><input type="number" min="0" max="10000" value={form.daily_upload_limit} onChange={(e) => setForm({ ...form, daily_upload_limit: parseInt(e.target.value) || 0 })} className="form-input" /><p className="form-hint">0–10,000</p></div>
              <div className="form-group"><label className="form-label">Daily Bytes Limit</label><input type="number" min="0" value={form.daily_bytes_limit} onChange={(e) => setForm({ ...form, daily_bytes_limit: parseInt(e.target.value) || 0 })} className="form-input" /><p className="form-hint">Default: 524,288,000 (500 MB). Max: ~100 GB</p></div>
            </div>
            <div className="modal-footer modal-footer-right">
              <button className="btn btn-secondary" onClick={closeModal} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : editQuota ? 'Save' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteId && (
        <div className="modal-overlay" onClick={() => !deleteLoading && setDeleteId(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h3 className="modal-title">Delete Quota</h3></div>
            <div className="modal-body"><p className="confirm-text">Delete quota <strong className="user-id-highlight">{deleteId}</strong>?</p><p className="confirm-subtext">This action cannot be undone.</p></div>
            <div className="modal-footer modal-footer-right">
              <button className="btn btn-secondary" onClick={() => setDeleteId(null)} disabled={deleteLoading}>Cancel</button>
              <button className="btn btn-danger" onClick={() => handleDelete(deleteId)} disabled={deleteLoading}>{deleteLoading ? 'Deleting...' : 'Delete'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirm */}
      {bulkDeleteConfirm && (
        <div className="modal-overlay" onClick={() => !bulkDeleteLoading && setBulkDeleteConfirm(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h3 className="modal-title">Delete Quotas</h3></div>
            <div className="modal-body"><p className="confirm-text">Delete <strong className="user-id-highlight">{selectedIds.size}</strong> selected quota(s)?</p><p className="confirm-subtext">This action cannot be undone.</p></div>
            <div className="modal-footer modal-footer-right">
              <button className="btn btn-secondary" onClick={() => setBulkDeleteConfirm(false)} disabled={bulkDeleteLoading}>Cancel</button>
              <button className="btn btn-danger" onClick={handleBulkDelete} disabled={bulkDeleteLoading}>{bulkDeleteLoading ? 'Deleting...' : `Delete ${selectedIds.size} Quota(s)`}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default QuotasTab;
